import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import { todayInMalaysia } from '@/lib/date'
import type { CalendarDay } from '@/types'
import Button from '@/components/ui/Button'

/** A month of the intake calendar. Every caller of the same month shares one cache entry. */
export function useCapacityCalendar(month: string) {
  return useQuery({
    queryKey: ['capacity', month],
    queryFn: async () => (await api.get<{ days: CalendarDay[] }>(`/capacity/calendar?month=${month}`)).data,
    enabled: /^\d{4}-\d{2}$/.test(month),
  })
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const date = new Date(y, m - 1 + delta, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function paddingFor(firstDate: string): unknown[] {
  const day = new Date(firstDate + 'T00:00:00').getDay()
  return Array.from({ length: (day + 6) % 7 })
}

/**
 * The production day picker for team consoles: closed, blocked and past days
 * are off, and so is any day without room for the pieces while the order
 * books the quota. A closed day stays closed even for a quota-exempt order -
 * the server refuses a day nobody is on the floor to start it. Used by the
 * manual order form and the reschedule dialog.
 */
export default function CapacityCalendar({
  month,
  onMonthChange,
  value,
  onChange,
  pieces,
  countsQuota,
  currentDate,
  heading,
  children,
}: {
  month: string
  onMonthChange: (month: string) => void
  value: string | null
  onChange: (date: string) => void
  pieces: number
  countsQuota: boolean
  /** The order's present production date when moving one: marked, and not selectable. */
  currentDate?: string | null
  heading?: ReactNode
  /** Rendered between the month bar and the grid. */
  children?: ReactNode
}) {
  const today = todayInMalaysia()
  const calendar = useCapacityCalendar(month)
  const needed = Math.max(1, pieces)
  const days = calendar.data?.days ?? []

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {heading ?? <span />}
        <div className="flex items-center gap-2">
          <Button variant="ghost" title="Previous month" onClick={() => onMonthChange(shiftMonth(month, -1))}>
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-sm font-semibold">{month}</span>
          <Button variant="ghost" title="Next month" onClick={() => onMonthChange(shiftMonth(month, 1))}>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
      {children}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="pb-1 text-center text-2xs font-bold uppercase text-muted">
            {d}
          </div>
        ))}
        {days.length > 0 && paddingFor(days[0].date).map((_, i) => <div key={`pad-${i}`} />)}
        {days.map((day) => {
          const full = day.remaining < needed
          const isCurrent = day.date === currentDate
          const disabled = day.past || !day.working || day.blocked || (countsQuota && full) || isCurrent
          const selected = value === day.date
          // The solid navy selection outranks the today ring.
          const isToday = !selected && day.date === today
          return (
            <button
              key={day.date}
              type="button"
              disabled={disabled}
              onClick={() => onChange(day.date)}
              aria-current={day.date === today ? 'date' : undefined}
              aria-pressed={selected}
              className={cn(
                'flex min-h-14 flex-col items-center justify-center rounded-lg border p-1 text-center',
                selected && 'border-primary bg-primary text-white',
                !selected && !disabled && 'border-line bg-surface hover:border-primary/50',
                !selected && disabled && 'cursor-not-allowed border-line/60 bg-app text-muted/50',
                isCurrent && 'border-dashed border-primary/60 text-primary',
                isToday && 'ring-2 ring-primary',
              )}
            >
              <span className={cn('text-sm font-bold', isToday && 'text-primary')}>{Number(day.date.slice(8))}</span>
              {isCurrent ? (
                <span className="text-2xs font-semibold">Now</span>
              ) : (
                countsQuota && day.working && !day.blocked && (
                  <span className="text-2xs">{full && !day.past ? 'Full' : `${day.remaining}`}</span>
                )
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
